import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import {
  defaultGridTuning,
  gridTuningForImageSize,
  type GridTuning,
} from "@/character/draft";
import { readImageFileAsDataUrl } from "@/character/frame";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  SHEET_COLUMNS,
  TEMPLATE_SHEET_HEIGHT,
  TEMPLATE_SHEET_WIDTH,
  describeSheetLayout,
  resolveSheetLayout,
  sheetLayoutFromImageLenient,
  supportsTemplateGridLayout,
} from "@/character/grid";
import {
  applySheetUploadClean,
  bindSheetUploadCleanControls,
  getSheetUploadCleanPrefs,
  readSheetUploadCleanPrefsFromHost,
  renderSheetUploadCleanControlsHtml,
} from "@/character/sheet-upload-clean";
import { flipImageDataUrlHorizontal } from "@/character/sheet-flip";
import { downscaleSheetDataUrlIfOverscaled } from "@/character/sheet-scale-down";
import { mountSheetUploadPreview } from "@/character/sheet-upload-preview";
import { downloadCharacterTemplate, downloadMotionTemplate } from "@/character/template";
import type { HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb } from "@/home/home-idb";
import { HomeEvents } from "@/home/home-events";
import {
  STAT_IDS,
  STAT_LABEL_TH,
  type StatAllocation,
  type StatId,
} from "@/game/model/character-stats";
import {
  SHADOW_AP_PER_CARD,
  SHADOW_BASE_AP,
  SHADOW_DISPLAY_ALPHA,
  SHADOW_SCALE_MULTIPLIER,
} from "@/shadow/constants";
import {
  mountShadowIdlePreview,
  mountShadowSpritePreview,
} from "@/shadow/mount-shadow-idle-preview";
import {
  equippedShadow,
  loadShadowState,
  removeShadowFromState,
  saveShadowState,
  updateShadowInState,
} from "@/shadow/firestore-storage";
import {
  allocateShadowStatPoint,
  applyShadowApCard,
  shadowApBudget,
  shadowApRemaining,
} from "@/shadow/shadow-ap";
import type { ShadowDefinition, ShadowPlayerState } from "@/shadow/types";
import { syncEquippedShadowPublic } from "@/shadow/sync-equipped-shadow-public";
import { appendShadowFromSpritesheet } from "@/shadow/create-shadow-from-sheet";
import {
  saveShadowSheetGridCreateSession,
  SHADOW_SHEET_GRID_CREATED_MESSAGE,
  SHADOW_SHEET_GRID_DONE_MESSAGE,
  takeShadowSheetGridResult,
} from "@/shadow/shadow-sheet-grid-result";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type MountShadowPanelOptions = {
  userId: string;
  getEconomy: () => HomeEconomy;
  onEconomyUpdated: (next: HomeEconomy) => void;
  /** When set, apply one AP card to this shadow on next render. */
  pendingApCardShadowId?: string | null;
  onPendingApCardConsumed?: () => void;
};

export type ShadowPanelHandle = {
  refresh: () => Promise<void>;
  /** Re-render UI (economy counts, create draft) without reloading Firestore. */
  repaint: () => void;
  teardown: () => void;
};

export function mountShadowPanel(
  host: HTMLElement,
  options: MountShadowPanelOptions,
): ShadowPanelHandle {
  let state: ShadowPlayerState = { version: 1, equippedId: null, shadows: [] };
  let selectedId: string | null = null;
  let status = "";
  let creating = false;
  let draftName = "ร่างเงา";
  let busy = false;
  let createError = "";
  let createWarnings: string[] = [];
  let createSheetSourceDataUrl: string | null = null;
  /** Pre-clean sheet after optional horizontal flip (for re-apply clean / preview). */
  let createSheetRawBeforeClean: string | null = null;
  /** True when uploaded art faces right and must be mirrored to game default (left). */
  let createFlipHorizontal = false;
  let createSpritesheetDataUrl: string | null = null;
  let createGridTuning: GridTuning = defaultGridTuning();
  let stopCreatePreview: (() => void) | undefined;
  let unbindCreateClean: (() => void) | undefined;
  let mountedPreviewSheetUrl: string | null = null;
  let stopCardPreviews: Array<() => void> = [];
  let statDraftShadowId: string | null = null;
  let statDraft: StatAllocation | null = null;

  const cloneStatAllocation = (allocation: StatAllocation): StatAllocation => ({
    ...allocation,
  });

  const syncStatDraftForSelected = () => {
    if (!selectedId) {
      statDraft = null;
      statDraftShadowId = null;
      return;
    }
    const shadow = state.shadows.find((s) => s.id === selectedId);
    if (!shadow) return;
    if (statDraftShadowId !== selectedId || !statDraft) {
      statDraft = cloneStatAllocation(shadow.statAllocation);
      statDraftShadowId = selectedId;
    }
  };

  const shadowWithStatDraft = (shadow: ShadowDefinition): ShadowDefinition => {
    if (statDraftShadowId === shadow.id && statDraft) {
      return { ...shadow, statAllocation: statDraft };
    }
    return shadow;
  };

  const isStatDraftDirty = (shadow: ShadowDefinition): boolean => {
    if (statDraftShadowId !== shadow.id || !statDraft) return false;
    return STAT_IDS.some((id) => statDraft![id] !== shadow.statAllocation[id]);
  };

  const resetStatDraftFromShadow = (shadow: ShadowDefinition) => {
    statDraft = cloneStatAllocation(shadow.statAllocation);
    statDraftShadowId = shadow.id;
  };

  const clearCardPreviews = () => {
    for (const stop of stopCardPreviews) stop();
    stopCardPreviews = [];
  };

  const mountShadowCardPreviews = (equipped: ShadowDefinition | null) => {
    clearCardPreviews();
    const equippedHost = host.querySelector<HTMLElement>("[data-shadow-equipped-preview]");
    if (equipped && equippedHost) {
      stopCardPreviews.push(
        mountShadowSpritePreview(equippedHost, equipped, {
          displaySizePx: 56,
          equippedStyle: true,
          showActionButtons: true,
        }),
      );
    }
    const detailHost = host.querySelector<HTMLElement>("[data-shadow-detail-preview]");
    const detailShadow =
      selectedId ? state.shadows.find((s) => s.id === selectedId) : undefined;
    if (detailShadow && detailHost) {
      stopCardPreviews.push(
        mountShadowSpritePreview(detailHost, detailShadow, {
          displaySizePx: 88,
          showActionButtons: true,
        }),
      );
    }
    host.querySelectorAll<HTMLElement>("[data-shadow-card-preview]").forEach((el) => {
      const id = el.dataset.shadowCardPreview;
      const shadow = id ? state.shadows.find((s) => s.id === id) : undefined;
      if (!shadow) return;
      stopCardPreviews.push(
        mountShadowIdlePreview(el, shadow, { displaySizePx: 56 }),
      );
    });
  };

  const syncCreateLayoutWarnings = (probe: HTMLImageElement) => {
    try {
      const layout = resolveSheetLayout(probe, createGridTuning);
      createWarnings = describeSheetLayout(layout).warnings;
    } catch {
      createWarnings = [];
    }
  };

  const rebuildCreateSpritesheetFromSource = async () => {
    if (!createSheetSourceDataUrl) return;
    let dataUrl = createSheetSourceDataUrl;
    if (createFlipHorizontal) {
      dataUrl = await flipImageDataUrlHorizontal(dataUrl);
    }
    createSheetRawBeforeClean = dataUrl;
    const cleanHost = host.querySelector<HTMLElement>("#shadow-create-sheet-upload-clean");
    const cleanPrefs = cleanHost
      ? readSheetUploadCleanPrefsFromHost(cleanHost)
      : getSheetUploadCleanPrefs();
    createSpritesheetDataUrl = await applySheetUploadClean(dataUrl, cleanPrefs);
    mountedPreviewSheetUrl = null;
    const probe = new Image();
    await new Promise<void>((resolve, reject) => {
      probe.onload = () => resolve();
      probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
      probe.src = dataUrl;
    });
    syncCreateLayoutWarnings(probe);
  };

  const resetCreateDraft = () => {
    createSheetSourceDataUrl = null;
    createSheetRawBeforeClean = null;
    createFlipHorizontal = false;
    createSpritesheetDataUrl = null;
    createGridTuning = defaultGridTuning();
    createError = "";
    createWarnings = [];
    stopCreatePreview?.();
    stopCreatePreview = undefined;
    unbindCreateClean?.();
    unbindCreateClean = undefined;
    mountedPreviewSheetUrl = null;
  };

  const updateCreateWarningMarkup = () => {
    const warnEl = host.querySelector<HTMLElement>(".shadow-create-warnings");
    if (!warnEl) return;
    if (createWarnings.length === 0) {
      warnEl.remove();
      return;
    }
    warnEl.innerHTML = createWarnings.map((w) => `<li>${escapeHtml(w)}</li>`).join("");
  };

  const mountCreatePreviewIfNeeded = () => {
    const previewHost = host.querySelector<HTMLElement>("#shadow-sheet-preview-host");
    if (!previewHost || !createSpritesheetDataUrl) return;
    if (mountedPreviewSheetUrl === createSpritesheetDataUrl && stopCreatePreview) {
      return;
    }
    stopCreatePreview?.();
    stopCreatePreview = undefined;
    mountedPreviewSheetUrl = createSpritesheetDataUrl;
    stopCreatePreview = mountSheetUploadPreview(
      previewHost,
      createSpritesheetDataUrl,
      createGridTuning,
      (gridTuning) => {
        createGridTuning = gridTuning;
        if (createSheetSourceDataUrl) {
          const probe = new Image();
          probe.onload = () => {
            syncCreateLayoutWarnings(probe);
            updateCreateWarningMarkup();
          };
          probe.src = createSheetRawBeforeClean ?? createSheetSourceDataUrl;
        }
      },
      {
        sourceDataUrl: createSheetRawBeforeClean ?? createSpritesheetDataUrl,
        onSheetDataUrlChange: (url) => {
          createSpritesheetDataUrl = url;
          mountedPreviewSheetUrl = url;
        },
        templateAlignMode: true,
      },
    );
  };

  const ingestCreateSheetDataUrl = async (dataUrl: string) => {
    createError = "";
    createWarnings = [];
    status = "กำลังประมวลผลสไปรต์ชีต…";
    render();
    try {
      const downscaled = await downscaleSheetDataUrlIfOverscaled(dataUrl);
      dataUrl = downscaled.dataUrl;
      const probe = new Image();
      await new Promise<void>((resolve, reject) => {
        probe.onload = () => resolve();
        probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
        probe.src = dataUrl;
      });
      sheetLayoutFromImageLenient(probe);
      createSheetSourceDataUrl = dataUrl;
      createFlipHorizontal = false;
      createGridTuning = gridTuningForImageSize(
        probe.naturalWidth,
        probe.naturalHeight,
        supportsTemplateGridLayout,
      );
      await rebuildCreateSpritesheetFromSource();
      const scaleNote =
        downscaled.appliedScale > 1
          ? `ลดขนาด ${downscaled.appliedScale}× เป็นเทมเพลตมาตรฐานแล้ว · `
          : "";
      status = createWarnings.length
        ? `${scaleNote}อัปโหลดแล้ว — ตรวจคำเตือนด้านล่าง · ปรับกริดได้`
        : `${scaleNote}อัปโหลดแล้ว — ปรับกริดด้านล่างได้`;
      render();
    } catch (e) {
      createSheetSourceDataUrl = null;
      createSpritesheetDataUrl = null;
      createGridTuning = defaultGridTuning();
      createWarnings = [];
      createError =
        e instanceof Error
          ? e.message
          : "อัปโหลดไม่สำเร็จ — ใช้เทมเพลต 672×384 px (7 คอลัมน์) หรือสไปรต์ล้วน 576×384 px";
      status = "";
      const fileInput = host.querySelector<HTMLInputElement>("#shadow-create-file");
      if (fileInput) fileInput.value = "";
      render();
    }
  };

  const processCreateSheetFile = async (file: File) => {
    const dataUrl = await readImageFileAsDataUrl(file);
    await ingestCreateSheetDataUrl(dataUrl);
  };

  const applyPendingGridSheet = async () => {
    const pending = takeShadowSheetGridResult();
    if (!pending) return;
    if (!creating) {
      creating = true;
      resetCreateDraft();
    }
    await ingestCreateSheetDataUrl(pending.dataUrl);
    status = "รับชีตจากกริดเฟรมแล้ว — ตรวจตัวอย่างก่อนบันทึก";
    render();
  };

  const persistState = async () => {
    await saveShadowState(options.userId, state);
    const equipped = equippedShadow(state);
    void syncEquippedShadowPublic(options.userId, equipped);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.ShadowChanged, {
        detail: { state, equipped },
      }),
    );
  };

  const renderStatRows = (shadow: ShadowDefinition): string => {
    const view = shadowWithStatDraft(shadow);
    const dirty = isStatDraftDirty(shadow);
    const rows = STAT_IDS
      .map((id) => {
        const pts = view.statAllocation[id];
        const canAdd = shadowApRemaining(view) > 0;
        return `<tr>
          <td>${escapeHtml(STAT_LABEL_TH[id])}</td>
          <td class="shadow-stat-pts">${pts}</td>
          <td>
            <button type="button" class="btn btn-ghost btn-xs" data-shadow-stat="${id}" ${canAdd && !busy ? "" : "disabled"}>+</button>
          </td>
        </tr>`;
      })
      .join("");
    const actions = dirty
      ? `<div class="shadow-stat-actions home-actions">
          <button type="button" class="btn btn-ghost btn-sm" data-shadow-stat-discard ${busy ? "disabled" : ""}>ยกเลิก</button>
          <button type="button" class="btn btn-primary btn-sm" data-shadow-stat-save ${busy ? "disabled" : ""}>บันทึกสเตต</button>
        </div>`
      : "";
    return `<table class="shadow-stat-table"><tbody>${rows}</tbody></table>${actions}`;
  };

  const render = () => {
    syncStatDraftForSelected();
    const eco = options.getEconomy();
    const equipped = equippedShadow(state);
    const selected = state.shadows.find((s) => s.id === selectedId) ?? null;
    const list = state.shadows
      .map((s) => {
        const on = s.id === state.equippedId;
        const sel = s.id === selectedId;
        return `<article class="shadow-card${sel ? " shadow-card--sel" : ""}${on ? " shadow-card--equipped" : ""}">
          <button type="button" class="shadow-card-btn" data-shadow-select="${escapeHtml(s.id)}">
            <div class="shadow-card-thumb" data-shadow-card-preview="${escapeHtml(s.id)}"></div>
            <div class="shadow-card-body">
              <span class="shadow-card-name">${escapeHtml(s.name)}</span>
              <span class="shadow-card-ap hint">AP ${shadowApRemaining(s)}/${shadowApBudget(s)}</span>
              ${on ? `<span class="shadow-equipped-tag">ใส่อยู่</span>` : ""}
            </div>
          </button>
        </article>`;
      })
      .join("");

    const equippedCard = equipped
      ? `<article class="shadow-equipped-card" aria-label="ร่างเงาที่ใส่อยู่">
          <header class="shadow-equipped-card-head">
            <span class="shadow-equipped-card-eyebrow">ตัวอย่างตอนใส่ในเกม</span>
            <h3 class="shadow-equipped-card-title">${escapeHtml(equipped.name)}</h3>
            <p class="hint shadow-equipped-card-hint">ใหญ่ ${SHADOW_SCALE_MULTIPLIER}× · จาง ${Math.round(SHADOW_DISPLAY_ALPHA * 100)}% · อยู่หน้าตัวละคร</p>
          </header>
          <div class="shadow-equipped-preview-wrap" data-shadow-equipped-preview></div>
        </article>`
      : `<p class="hint shadow-none-equipped">ยังไม่ได้ใส่ร่างเงา — เลือกการ์ดด้านล่างแล้วกดใส่</p>`;

    const actionList = CHARACTER_ACTIONS
      .map(
        (action) =>
          `<li><strong>${escapeHtml(action.templateLabel)}</strong> — ${escapeHtml(action.hint)}</li>`,
      )
      .join("");
    const createPreviewBlock = createSpritesheetDataUrl
      ? `<div id="shadow-sheet-preview-host"></div>`
      : `<div class="sheet-preview sheet-preview--empty">ยังไม่ได้อัปโหลดสไปรต์ชีต</div>`;
    const canSubmitCreate = Boolean(
      draftName.trim() && createSpritesheetDataUrl && !busy && !createError,
    );
    const createWarningMarkup = createWarnings.length
      ? `<ul class="grid-panel-warnings shadow-create-warnings" role="alert">
          ${createWarnings.map((w) => `<li>${escapeHtml(w)}</li>`).join("")}
        </ul>`
      : "";
    const createErrorMarkup = createError
      ? `<p class="form-error shadow-create-error" role="alert">${escapeHtml(createError)}</p>`
      : "";

    const createBlock = creating
      ? `
        <div class="shadow-create-form character-card character-card--wide">
          <h4 class="shadow-section-title">สร้างร่างเงาใหม่</h4>
          <p class="hint">
            แต่ละแถว = การเคลื่อนไหว 1 แบบ · ${GRID_COLUMNS} ภาพต่อแถว · รวม ${GRID_ROWS} แถว
            (เทมเพลต ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} px · กริด ${SHEET_COLUMNS} คอลัมน์)
          </p>
          <div class="template-steps">
            <ol class="template-step-list">
              <li>กด <strong>โหลดเทมเพลตทั้งแผ่น</strong> (คอลัมน์ 1 = ป้าย IDLE/RUN/…)</li>
              <li>วางสไปรต์ในคอลัมน์ 2–7 — แถวละ ${GRID_COLUMNS} เฟรม</li>
              <li>Export PNG ตามเทมเพลต (หรือ 2×/3×) · อัปโหลดแล้วลากกริดทับแถว action · ซูมดู · ดู loop ก่อนบันทึก</li>
            </ol>
            <ul class="action-row-list">${actionList}</ul>
          </div>
          <div class="template-actions">
            <button type="button" id="shadow-download-template" class="btn btn-ghost">โหลดเทมเพลตทั้งแผ่น</button>
            <a class="btn btn-ghost" href="/assets/images/shadow-sprite-sheet-example.png" target="_blank" rel="noopener">
              ดูตัวอย่าง (672×384)
            </a>
            <a class="btn btn-ghost" href="/assets/images/shadow-sprite-sheet-example.png" download="shadow-sprite-sheet-672x384.png">
              ดาวน์โหลดไฟล์พร้อมอัปโหลด
            </a>
          </div>
          <div class="template-motion-downloads">
            <p class="hint">เทมเพลตทีละการเคลื่อนไหว (1×${GRID_COLUMNS}):</p>
            <div class="template-actions">
              ${CHARACTER_ACTIONS
                .map(
                  (action) =>
                    `<button type="button" class="btn btn-ghost" data-shadow-motion="${action.key}">${action.templateLabel} ×${GRID_COLUMNS}</button>`,
                )
                .join("")}
            </div>
          </div>
          <label class="field">
            <span class="field-label">ชื่อร่างเงา</span>
            <input type="text" class="input" id="shadow-create-name" maxlength="48" value="${escapeHtml(draftName)}" />
          </label>
          <div id="shadow-create-sheet-upload-clean">${renderSheetUploadCleanControlsHtml("shadow-create-clean")}</div>
          <div class="template-actions shadow-create-upload-row">
            <label class="btn btn-ghost sheet-upload">
              อัปโหลดสไปรต์ชีต
              <input type="file" id="shadow-create-file" accept="image/png,image/jpeg,image/webp" hidden />
            </label>
            <button type="button" class="btn btn-ghost" id="shadow-open-frame-grid">
              อัปโหลดทีละเฟรม (แท็บใหม่)
            </button>
          </div>
          ${
            createSheetSourceDataUrl
              ? `<div class="shadow-facing-tools">
            <p class="hint">ทิศตัวในเกม — หันซ้ายเป็นค่าเริ่มต้น (เหมือนตัวละครหลัก)</p>
            <div class="template-actions">
              <button type="button" class="btn btn-ghost shadow-facing-btn${!createFlipHorizontal ? " shadow-facing-btn--active" : ""}" data-shadow-facing="left">ไฟล์หันซ้ายแล้ว</button>
              <button type="button" class="btn btn-ghost shadow-facing-btn${createFlipHorizontal ? " shadow-facing-btn--active" : ""}" data-shadow-facing="flip">กลับด้าน (ไฟล์หนขวา)</button>
            </div>
          </div>`
              : ""
          }
          ${createErrorMarkup}
          ${createWarningMarkup}
          ${createPreviewBlock}
          <div class="home-actions">
            <button type="button" class="btn btn-primary" id="shadow-create-submit" ${canSubmitCreate ? "" : "disabled"}>บันทึกร่างเงา</button>
            <button type="button" class="btn btn-ghost" id="shadow-create-cancel" ${busy ? "disabled" : ""}>ยกเลิก</button>
          </div>
        </div>`
      : `<div class="shadow-start-create-row home-actions">
          <button type="button" class="btn btn-primary" id="shadow-start-create" ${eco.shadowCreateCards < 1 ? "disabled" : ""}>
            สร้างที่นี่
          </button>
          <button type="button" class="btn btn-ghost" id="shadow-start-create-grid" ${eco.shadowCreateCards < 1 ? "disabled" : ""}>
            สร้างผ่านกริด (แท็บใหม่)
          </button>
          <span class="hint shadow-start-create-hint">บัตรสร้าง ${eco.shadowCreateCards}</span>
        </div>`;

    const selectedView = selected ? shadowWithStatDraft(selected) : null;
    const selectedStatDirty = selected ? isStatDraftDirty(selected) : false;
    const detail = selected && selectedView
      ? `
        <article class="shadow-detail-card${selectedStatDirty ? " shadow-detail-card--dirty" : ""}">
          <h3 class="shadow-detail-title">${escapeHtml(selected.name)}</h3>
          <p class="hint">AP ${shadowApRemaining(selectedView)} / ${shadowApBudget(selected)} (เริ่ม ${SHADOW_BASE_AP} + บัตร ${selected.apCardsApplied}×${SHADOW_AP_PER_CARD})${selectedStatDirty ? " · <strong>ยังไม่บันทึกสเตต</strong>" : ""}</p>
          <div class="shadow-detail-preview" data-shadow-detail-preview></div>
          ${renderStatRows(selected)}
          <div class="home-actions">
            <button type="button" class="btn btn-ghost" data-shadow-ap="${escapeHtml(selected.id)}" ${eco.shadowApCards < 1 ? "disabled" : ""}>ใช้บัตร AP (+${SHADOW_AP_PER_CARD})</button>
            ${
              state.equippedId === selected.id
                ? `<button type="button" class="btn btn-ghost" data-shadow-unequip>ถอดร่างเงา</button>`
                : `<button type="button" class="btn btn-primary" data-shadow-equip="${escapeHtml(selected.id)}">ใส่ร่างเงา</button>`
            }
            <button type="button" class="btn btn-danger btn-ghost" data-shadow-delete="${escapeHtml(selected.id)}" ${busy ? "disabled" : ""}>ลบร่างเงา</button>
          </div>
        </article>`
      : "";

    host.innerHTML = `
      <section class="shadow-panel" aria-label="ร่างเงา">
        <p class="hint">คลังสะสม · ใส่ได้ 1 ตัว · ลบได้ (ไม่คืนบัตรสร้าง)</p>
        ${equippedCard}
        <h4 class="shadow-section-title">คลังสะสม (${state.shadows.length})</h4>
        <div class="shadow-collection-grid">${list || `<p class="hint">ยังไม่มีร่างเงา</p>`}</div>
        ${detail}
        ${createBlock}
        ${status ? `<p class="hint shadow-status">${escapeHtml(status)}</p>` : ""}
      </section>`;

    stopCreatePreview?.();
    stopCreatePreview = undefined;
    unbindCreateClean?.();
    unbindCreateClean = undefined;

    if (creating) {
      const cleanHost = host.querySelector<HTMLElement>("#shadow-create-sheet-upload-clean");
      if (cleanHost) {
        unbindCreateClean = bindSheetUploadCleanControls(cleanHost, () => {
          void (async () => {
            try {
              await rebuildCreateSpritesheetFromSource();
              render();
            } catch {
              /* ignore */
            }
          })();
        });
      }
      mountCreatePreviewIfNeeded();
    }
    mountShadowCardPreviews(equipped);
  };

  const applyApToShadow = async (shadowId: string) => {
    const shadow = state.shadows.find((s) => s.id === shadowId);
    if (!shadow) return;
    const result = applyShadowApCard(options.getEconomy(), shadow);
    if (!result.ok) {
      status = result.reason;
      render();
      return;
    }
    options.onEconomyUpdated(result.economy);
    state = updateShadowInState(state, result.shadow);
    if (selectedId === result.shadow.id) {
      resetStatDraftFromShadow(result.shadow);
    }
    busy = true;
    try {
      await persistState();
      status = `ใช้บัตร AP กับ ${result.shadow.name} แล้ว`;
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const selectBtn = target.closest<HTMLElement>("[data-shadow-select]");
    if (selectBtn?.dataset.shadowSelect) {
      const nextId = selectBtn.dataset.shadowSelect;
      const current = selectedId ? state.shadows.find((s) => s.id === selectedId) : undefined;
      if (
        selectedId &&
        nextId !== selectedId &&
        current &&
        isStatDraftDirty(current) &&
        !window.confirm("ยังไม่บันทึกสเตต — เปลี่ยนร่างเงาและทิ้งการแก้ไข?")
      ) {
        return;
      }
      selectedId = nextId;
      statDraftShadowId = null;
      status = "";
      render();
      return;
    }
    if (target.closest("#shadow-start-create")) {
      creating = true;
      status = "";
      createError = "";
      createWarnings = [];
      resetCreateDraft();
      render();
      return;
    }
    if (target.closest("#shadow-start-create-grid")) {
      const eco = options.getEconomy();
      if (eco.shadowCreateCards < 1) {
        status = "ไม่มีบัตรสร้างร่างเงา";
        render();
        return;
      }
      saveShadowSheetGridCreateSession(draftName);
      window.open("/shadow/sheet-grid?create=1", "_blank", "noopener");
      status =
        "เปิดแท็บกริดแล้ว — กรอกเฟรมครบแล้วกด «สร้างร่างเงา» ในแท็บนั้นได้เลย";
      render();
      return;
    }
    if (target.closest("#shadow-create-cancel")) {
      creating = false;
      resetCreateDraft();
      render();
      return;
    }
    if (target.closest("#shadow-create-submit")) {
      void submitCreate();
      return;
    }
    if (target.closest("#shadow-download-template")) {
      downloadCharacterTemplate();
      return;
    }
    if (target.closest("#shadow-open-frame-grid")) {
      window.open("/shadow/sheet-grid", "_blank", "noopener");
      status =
        "เปิดแท็บกริดแล้ว — กรอกครบ 24 เฟรมแล้วกด «ใช้ชีตนี้และกลับ» ชีตจะโหลดที่นี่อัตโนมัติ";
      render();
      return;
    }
    const motionBtn = target.closest<HTMLElement>("[data-shadow-motion]");
    if (motionBtn?.dataset.shadowMotion) {
      downloadMotionTemplate(motionBtn.dataset.shadowMotion as CharacterActionKey);
      return;
    }
    const facingBtn = target.closest<HTMLElement>("[data-shadow-facing]");
    if (facingBtn?.dataset.shadowFacing && createSheetSourceDataUrl) {
      const mode = facingBtn.dataset.shadowFacing;
      const nextFlip = mode === "flip";
      if (nextFlip === createFlipHorizontal) return;
      createFlipHorizontal = nextFlip;
      void (async () => {
        busy = true;
        status = "กำลังกลับด้านสไปรต์…";
        render();
        try {
          await rebuildCreateSpritesheetFromSource();
          status = createFlipHorizontal
            ? "กลับด้านแล้ว — ตัวหันซ้ายตามระบบ"
            : "ใช้ทิศทางจากไฟล์ต้นฉบับ";
        } catch (e) {
          createError = e instanceof Error ? e.message : "กลับด้านไม่สำเร็จ";
          status = "";
        } finally {
          busy = false;
          render();
        }
      })();
      return;
    }
    const equipBtn = target.closest<HTMLElement>("[data-shadow-equip]");
    if (equipBtn?.dataset.shadowEquip) {
      void equip(equipBtn.dataset.shadowEquip);
      return;
    }
    if (target.closest("[data-shadow-unequip]")) {
      void equip(null);
      return;
    }
    const apBtn = target.closest<HTMLElement>("[data-shadow-ap]");
    if (apBtn?.dataset.shadowAp) {
      void applyApToShadow(apBtn.dataset.shadowAp);
      return;
    }
    const statBtn = target.closest<HTMLElement>("[data-shadow-stat]");
    if (statBtn?.dataset.shadowStat && selectedId) {
      bumpStatDraft(statBtn.dataset.shadowStat as StatId);
      return;
    }
    if (target.closest("[data-shadow-stat-discard]")) {
      discardStatDraft();
      return;
    }
    if (target.closest("[data-shadow-stat-save]")) {
      void saveStatDraft();
      return;
    }
    const deleteBtn = target.closest<HTMLElement>("[data-shadow-delete]");
    if (deleteBtn?.dataset.shadowDelete) {
      void deleteShadow(deleteBtn.dataset.shadowDelete);
    }
  };

  const deleteShadow = async (shadowId: string) => {
    const shadow = state.shadows.find((s) => s.id === shadowId);
    if (!shadow) return;
    const equippedNote =
      state.equippedId === shadowId ? "\n(จะถอดออกจากตัวละครด้วย)" : "";
    if (
      !window.confirm(
        `ลบร่างเงา "${shadow.name}" ถาวร?\nAP และบัตรที่ใช้ไปแล้วไม่คืน${equippedNote}`,
      )
    ) {
      return;
    }
    busy = true;
    render();
    try {
      state = removeShadowFromState(state, shadowId);
      if (selectedId === shadowId) {
        selectedId = state.shadows[0]?.id ?? null;
      }
      await persistState();
      status = `ลบ ${shadow.name} แล้ว`;
    } catch {
      status = "ลบไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const equip = async (shadowId: string | null) => {
    state = { ...state, equippedId: shadowId };
    busy = true;
    try {
      await persistState();
      status = shadowId ? "ใส่ร่างเงาแล้ว" : "ถอดร่างเงาแล้ว";
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const bumpStatDraft = (statId: StatId) => {
    if (!selectedId || busy) return;
    const shadow = state.shadows.find((s) => s.id === selectedId);
    if (!shadow) return;
    syncStatDraftForSelected();
    const view = shadowWithStatDraft(shadow);
    const next = allocateShadowStatPoint(view, statId);
    if (!next) return;
    statDraft = cloneStatAllocation(next.statAllocation);
    statDraftShadowId = shadow.id;
    render();
  };

  const discardStatDraft = () => {
    if (!selectedId) return;
    const shadow = state.shadows.find((s) => s.id === selectedId);
    if (!shadow) return;
    resetStatDraftFromShadow(shadow);
    status = "";
    render();
  };

  const saveStatDraft = async () => {
    if (!selectedId || busy) return;
    const shadow = state.shadows.find((s) => s.id === selectedId);
    if (!shadow || !statDraft || !isStatDraftDirty(shadow)) return;
    const next = { ...shadow, statAllocation: cloneStatAllocation(statDraft) };
    state = updateShadowInState(state, next);
    busy = true;
    render();
    try {
      await persistState();
      resetStatDraftFromShadow(next);
      status = "บันทึกสเตตร่างเงาแล้ว";
    } catch {
      status = "บันทึกไม่สำเร็จ";
    } finally {
      busy = false;
      render();
    }
  };

  const submitCreate = async () => {
    const eco = options.getEconomy();
    if (eco.shadowCreateCards < 1) {
      status = "ไม่มีบัตรสร้างร่างเงา";
      render();
      return;
    }
    const nameInput = host.querySelector<HTMLInputElement>("#shadow-create-name");
    const name = (nameInput?.value ?? draftName).trim() || "ร่างเงา";
    if (!createSpritesheetDataUrl) {
      createError = "อัปโหลดสไปรต์ชีตและดูตัวอย่างก่อนบันทึก";
      status = "";
      render();
      return;
    }
    createError = "";
    busy = true;
    status = "กำลังประมวลผล…";
    render();
    try {
      await rebuildCreateSpritesheetFromSource();
      const sheetUrl = createSpritesheetDataUrl;
      if (!sheetUrl) {
        createError = "อัปโหลดสไปรต์ชีตและดูตัวอย่างก่อนบันทึก";
        status = "";
        return;
      }
      const result = await appendShadowFromSpritesheet(state, eco, {
        name,
        sheetDataUrl: sheetUrl,
        gridTuning: createGridTuning,
      });
      if (!result.ok) {
        createError = result.reason;
        status = "";
        return;
      }
      state = result.state;
      selectedId = result.shadow.id;
      options.onEconomyUpdated(result.economy);
      await persistState();
      creating = false;
      resetCreateDraft();
      status = `สร้าง ${result.shadow.name} แล้ว`;
    } catch (e) {
      createError = e instanceof Error ? e.message : "สร้างร่างเงาไม่สำเร็จ";
      status = "";
    } finally {
      busy = false;
      render();
    }
  };

  const onHostChange = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.id !== "shadow-create-file") return;
    if (!creating) return;
    const file = input.files?.[0];
    if (!file) return;
    void processCreateSheetFile(file);
  };

  const onHostInput = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.id !== "shadow-create-name") return;
    draftName = input.value;
  };

  const onGridMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === SHADOW_SHEET_GRID_DONE_MESSAGE) {
      void applyPendingGridSheet();
      return;
    }
    if (event.data?.type === SHADOW_SHEET_GRID_CREATED_MESSAGE) {
      const name =
        typeof event.data.name === "string" ? event.data.name : "ร่างเงา";
      void (async () => {
        creating = false;
        resetCreateDraft();
        const eco = await loadHomeEconomyFromIdb(options.userId);
        if (eco) options.onEconomyUpdated(eco);
        await refresh();
        status = `สร้าง ${name} จากแท็บกริดแล้ว`;
        render();
      })();
    }
  };

  const onGridFocus = () => {
    void applyPendingGridSheet();
  };

  host.addEventListener("click", onClick);
  host.addEventListener("change", onHostChange);
  host.addEventListener("input", onHostInput);
  window.addEventListener("message", onGridMessage);
  window.addEventListener("focus", onGridFocus);
  void applyPendingGridSheet();

  const refresh = async () => {
    state = await loadShadowState(options.userId);
    statDraftShadowId = null;
    if (!selectedId && state.shadows[0]) selectedId = state.shadows[0].id;
    render();
    const pending = options.pendingApCardShadowId;
    if (pending && state.shadows.some((s) => s.id === pending)) {
      selectedId = pending;
      render();
      await applyApToShadow(pending);
      options.onPendingApCardConsumed?.();
    }
  };

  void refresh();

  return {
    refresh,
    repaint: () => render(),
    teardown: () => {
      host.removeEventListener("click", onClick);
      host.removeEventListener("change", onHostChange);
      host.removeEventListener("input", onHostInput);
      window.removeEventListener("message", onGridMessage);
      window.removeEventListener("focus", onGridFocus);
      resetCreateDraft();
      clearCardPreviews();
      host.replaceChildren();
    },
  };
}
